import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { resolveOrganizationOnboarding, type OrganizationSegment } from '../organizationOnboarding';
import { 
  User, 
  Organization, 
  Subscription, 
  AuthState, 
  AuthContextType, 
  SignUpData, 
  UserRole, 
  ROLE_PERMISSIONS 
} from '../../types/auth';

export const DEMO_LOGIN_ENABLED = import.meta.env.VITE_ALLOW_DEMO_LOGIN === 'true';

const demoOrganizations: Organization[] = [
  {
    id: 'org_1',
    name: 'Acme Corp',
    slug: 'acme-corp',
    plan: 'professional',
    industry: 'fintech',
    size: 'medium',
    settings: {
      allowSSOLogin: true,
      requireMFA: false,
      apiKeyExpiration: 90,
      maxTeamMembers: 20,
      allowedDomains: ['example.com'],
      webhookEndpoints: ['https://api.example.com/webhooks']
    },
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-10-15')
  }
];

const demoUsers: User[] = [
  {
    id: 'user_1',
    email: 'account.owner@example.com',
    firstName: 'John',
    lastName: 'Smith',
    role: 'admin',
    organizationId: 'org_1',
    status: 'active',
    lastLogin: new Date(),
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-10-21')
  },
  {
    id: 'user_2',
    email: 'developer@example.com',
    firstName: 'Sarah',
    lastName: 'Johnson',
    role: 'developer',
    organizationId: 'org_1',
    status: 'active',
    lastLogin: new Date(Date.now() - 86400000), // yesterday
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-10-20')
  }
];

const demoSubscriptions: Subscription[] = [
  {
    id: 'sub_1',
    organizationId: 'org_1',
    plan: 'professional',
    status: 'active',
    currentPeriodStart: new Date('2024-10-01'),
    currentPeriodEnd: new Date('2024-11-01'),
    cancelAtPeriodEnd: false,
    usage: {
      apiCalls: 45230,
      monthlyLimit: 100000
    },
    decisionControl: {
      mode: 'HUMAN_IN_LOOP',
      regulatedBankPolicy: false,
      auditLoggingLocked: true,
      allowAutoApproveLowRiskKyc: true,
      allowAutoRejectFraud: false,
      auditRetentionDays: 365,
      policyVersion: 'org-policy-v1'
    }
  }
];

// Auth reducer
type AuthAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SIGN_IN_SUCCESS'; payload: { user: User; organization: Organization; subscription: Subscription } }
  | { type: 'SIGN_OUT' }
  | { type: 'UPDATE_PROFILE'; payload: Partial<User> }
  | { type: 'SWITCH_ORGANIZATION'; payload: { organization: Organization; subscription: Subscription } };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SIGN_IN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        organization: action.payload.organization,
        subscription: action.payload.subscription,
        isAuthenticated: true,
        loading: false
      };
    
    case 'SIGN_OUT':
      return {
        user: null,
        organization: null,
        subscription: null,
        loading: false,
        isAuthenticated: false
      };
    
    case 'UPDATE_PROFILE':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null
      };
    
    case 'SWITCH_ORGANIZATION':
      return {
        ...state,
        organization: action.payload.organization,
        subscription: action.payload.subscription
      };
    
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  organization: null,
  subscription: null,
  loading: true,
  isAuthenticated: false
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        if (!DEMO_LOGIN_ENABLED) {
          localStorage.removeItem('wardya_auth');
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        const storedAuth = localStorage.getItem('wardya_auth');
        if (storedAuth) {
          const { userId, organizationId } = JSON.parse(storedAuth);
          
          const user = demoUsers.find(u => u.id === userId);
          const organization = demoOrganizations.find(o => o.id === organizationId);
          const subscription = demoSubscriptions.find(s => s.organizationId === organizationId);
          
          if (user && organization && subscription) {
            dispatch({ 
              type: 'SIGN_IN_SUCCESS', 
              payload: { user, organization, subscription } 
            });
          } else {
            localStorage.removeItem('wardya_auth');
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('wardya_auth');
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuthState();
  }, []);

  const signIn = async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      if (!DEMO_LOGIN_ENABLED) {
        throw new Error('Enterprise SSO is required');
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      if (!password || password.length < 6) {
        throw new Error('Invalid credentials');
      }
      
      const user = demoUsers.find(u => u.email === email);
      if (!user) {
        throw new Error('Invalid credentials');
      }
      
      const organization = demoOrganizations.find(o => o.id === user.organizationId);
      const subscription = demoSubscriptions.find(s => s.organizationId === user.organizationId);
      
      if (!organization || !subscription) {
        throw new Error('Organization data not found');
      }
      
      // Store auth data
      localStorage.setItem('wardya_auth', JSON.stringify({
        userId: user.id,
        organizationId: organization.id
      }));
      
      // Update last login
      user.lastLogin = new Date();
      
      dispatch({ 
        type: 'SIGN_IN_SUCCESS', 
        payload: { user, organization, subscription } 
      });
      
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const signInWithProvider = async (provider: 'github') => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      if (!DEMO_LOGIN_ENABLED) {
        throw new Error('Enterprise SSO is required');
      }

      await new Promise(resolve => setTimeout(resolve, 900));

      if (provider !== 'github') {
        throw new Error('Unsupported provider');
      }

      const user = demoUsers.find(u => u.email === 'developer@example.com');
      if (!user) {
        throw new Error('Provider account not found');
      }

      const organization = demoOrganizations.find(o => o.id === user.organizationId);
      const subscription = demoSubscriptions.find(s => s.organizationId === user.organizationId);

      if (!organization || !subscription) {
        throw new Error('Organization data not found');
      }

      localStorage.setItem('wardya_auth', JSON.stringify({
        userId: user.id,
        organizationId: organization.id
      }));

      user.lastLogin = new Date();

      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { user, organization, subscription }
      });
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const signOut = async () => {
    localStorage.removeItem('wardya_auth');
    dispatch({ type: 'SIGN_OUT' });
  };

  const signUp = async (userData: SignUpData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      if (!DEMO_LOGIN_ENABLED) {
        throw new Error('Enterprise SSO is required');
      }

      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const onboardingPolicy = resolveOrganizationOnboarding(userData.industry as OrganizationSegment);

      // Create new organization
      const newOrg: Organization = {
        id: `org_${Date.now()}`,
        name: userData.organizationName,
        slug: userData.organizationName.toLowerCase().replace(/\s+/g, '-'),
        plan: 'free',
        industry: userData.industry,
        productLine: onboardingPolicy.productLine,
        operatingModel: onboardingPolicy.operatingModel,
        rolePack: onboardingPolicy.rolePack,
        provisionedRoles: onboardingPolicy.defaultRoles,
        size: userData.companySize as any,
        settings: {
          allowSSOLogin: false,
          requireMFA: false,
          apiKeyExpiration: 30,
          maxTeamMembers: 2,
          allowedDomains: [],
          webhookEndpoints: []
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Create new user
      const newUser: User = {
        id: `user_${Date.now()}`,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: 'admin',
        organizationId: newOrg.id,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Create subscription
      const newSubscription: Subscription = {
        id: `sub_${Date.now()}`,
        organizationId: newOrg.id,
        plan: 'free',
        status: 'active',
        currentPeriodStart: new Date(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        cancelAtPeriodEnd: false,
      usage: {
        apiCalls: 0,
        monthlyLimit: 1000
      },
      decisionControl: {
        mode: 'AI_ASSISTED',
        regulatedBankPolicy: false,
        auditLoggingLocked: true,
        allowAutoApproveLowRiskKyc: false,
        allowAutoRejectFraud: false,
        auditRetentionDays: 180,
        policyVersion: 'org-policy-v1'
      }
    };
      
      // Add to demo data
      demoOrganizations.push(newOrg);
      demoUsers.push(newUser);
      demoSubscriptions.push(newSubscription);
      
      // Store auth data
      localStorage.setItem('wardya_auth', JSON.stringify({
        userId: newUser.id,
        organizationId: newOrg.id
      }));
      
      dispatch({ 
        type: 'SIGN_IN_SUCCESS', 
        payload: { user: newUser, organization: newOrg, subscription: newSubscription } 
      });
      
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!state.user) return;
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const updatedUser = { ...state.user, ...data, updatedAt: new Date() };
    
    // Update in demo data
    const userIndex = demoUsers.findIndex(u => u.id === state.user!.id);
    if (userIndex !== -1) {
      demoUsers[userIndex] = updatedUser;
    }
    
    dispatch({ type: 'UPDATE_PROFILE', payload: data });
  };

  const switchOrganization = async (orgId: string) => {
    if (!state.user) return;
    
    const organization = demoOrganizations.find(o => o.id === orgId);
    const subscription = demoSubscriptions.find(s => s.organizationId === orgId);
    
    if (!organization || !subscription) {
      throw new Error('Organization not found');
    }
    
    localStorage.setItem('wardya_auth', JSON.stringify({
      userId: state.user.id,
      organizationId: orgId
    }));
    
    dispatch({ 
      type: 'SWITCH_ORGANIZATION', 
      payload: { organization, subscription } 
    });
  };

  const hasPermission = (permission: string): boolean => {
    if (!state.user) return false;
    
    const userPermissions = ROLE_PERMISSIONS[state.user.role] || [];
    
    return userPermissions.some(p => {
      if (p.endsWith(':*')) {
        const prefix = p.slice(0, -2);
        return permission.startsWith(prefix);
      }
      return p === permission;
    });
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!state.user) return false;
    
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(state.user.role);
  };

  const value: AuthContextType = {
    ...state,
    signIn,
    signInWithProvider,
    signOut,
    signUp,
    updateProfile,
    switchOrganization,
    hasPermission,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export demo data for development
export { demoUsers, demoOrganizations, demoSubscriptions };
